import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useSessionUser } from '@/hooks/use-session'
import * as v from 'valibot'
import { useLingui } from '@lingui/react/macro'
import { Button } from '@/components/ui/button'
import { ArrowRightIcon, BriefcaseIcon, FileTextIcon, UsersIcon, type LucideIcon } from 'lucide-react'
import { RegistrationSteps } from './-components/shared/registration-steps'
import { indicatorPalette, type IndicatorType } from './-components/shared/indicator-colors'
import { Appear } from '@/components/custom/appear'
import { cn, flowViewTransition } from '@/lib/utils'
import { IntroStepPending } from './-components/shared/flow-pending'

export const Route = createFileRoute('/_app/test/')({
  // `redirect` is carried through the whole onboarding (from the compare CTA) so it survives the test
  // steps and lands the user back on their intended destination at the end.
  validateSearch: v.object({ redirect: v.optional(v.string()) }),
  component: RouteComponent,
  pendingComponent: IntroStepPending,
})

function RouteComponent() {
  const { t } = useLingui()
  const user = useSessionUser()
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()

  // Logged-out → /login is enforced centrally by the `_app` layout guard (see route-access); a logged-in
  // non-applicant (a company account) has no test, so render nothing.
  if (!user || user.type !== 'applicant') return null

  const anyStarted = !!user.testPassed || !!user.testPassedValues || !!user.testPassedPersonality
  // Always advance linearly from the intro to the first test. Skipping happens only at login.
  const onPrimary = () =>
    navigate({ to: '/test/competences', search: { redirect }, viewTransition: flowViewTransition() })

  // Gender the welcome to the applicant when we know their sex; fall back to a neutral form otherwise.
  const welcome =
    user.sex === 'male'
      ? t({ context: 'male', message: "Welcome to D'Anchiano" })
      : user.sex === 'female'
        ? t({ context: 'female', message: "Welcome to D'Anchiano" })
        : t({ context: 'neutral', message: "Welcome to D'Anchiano" })

  // The three questionnaires, in the order they're taken — each line says what that questionnaire reveals.
  const steps = [
    { type: 'competences', label: t`Competencies`, hint: t`Your preferred knowledge, skills and attitudes.` },
    { type: 'values', label: t`Values`, hint: t`Which principles, interests and drivers you prioritize in your work.` },
    {
      type: 'personality',
      label: t`Personality`,
      hint: t`Which traits, behaviors and drivers define your character structure.`,
    },
  ] satisfies { type: IndicatorType; label: string; hint: string }[]

  const perks: { icon: LucideIcon; title: string; desc: string }[] = [
    {
      icon: FileTextIcon,
      // Self-registered professionals only get this one card, so it reads as "your report"; a
      // company employee gets three, so it's one report "of your profile" among them.
      title: user.isEmployee ? t`A report of your profile` : t`Your profile report`,
      desc: t`With the results, charts and descriptions derived from your answers.`,
    },
    // The position and team reports only exist for employees invited by a company; a self-registered
    // professional has neither a company-assigned position nor teams, so hide them.
    ...(user.isEmployee
      ? [
          {
            icon: BriefcaseIcon,
            title: t`A report of your position's profile`,
            desc: t`With the psychographic requirements set by your company and those of the market.`,
          },
          {
            icon: UsersIcon,
            title: t`A report of your teams' profile`,
            desc: t`With the psychographic profiles of each of their members and of all of them together (subject to permissions).`,
          },
        ]
      : []),
  ]

  return (
    <section className="flex flex-col gap-3">
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-6 px-4 py-6">
        <RegistrationSteps user={user} stage={{ kind: 'intro' }} />

        <div className="flow-header text-center">
          <h1 className="text-2xl font-semibold">{welcome}</h1>
          <p className="text-muted-foreground mt-1">{t`Let's start by building your psychographic profile`}</p>
        </div>

        {/* HOW IT WORKS — complete the three questionnaires; each reveals a part of your profile */}
        <div className="flex w-full flex-col gap-3">
          <span className="text-muted-foreground px-1 text-xs font-semibold tracking-wider uppercase">
            {t`How it works`}
          </span>
          <p className="text-muted-foreground px-1 text-sm">
            {t`To build your profile, you'll complete three short questionnaires in these areas:`}
          </p>
          <ul className="flex flex-col gap-3">
            {steps.map((item, index) => {
              const palette = indicatorPalette[item.type]
              const Icon = palette.icon
              return (
                <Appear
                  as="li"
                  key={item.type}
                  delay={index * 140}
                  className="bg-card flex items-center gap-4 rounded-xl border p-4"
                >
                  <div className="relative shrink-0">
                    <div
                      className={cn(
                        'flex size-12 items-center justify-center rounded-xl',
                        palette.activeBg,
                        palette.activeText,
                      )}
                    >
                      <Icon className="size-6" strokeWidth={1.5} />
                    </div>
                    <span className="bg-foreground text-background absolute -top-2 -left-2 flex size-6 items-center justify-center rounded-full text-xs font-semibold">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="font-semibold">{item.label}</span>
                    <span className="text-muted-foreground text-sm">{item.hint}</span>
                  </div>
                </Appear>
              )
            })}
          </ul>
        </div>

        {/* WHAT YOU GET — the concrete deliverables once your profile is built */}
        <div className="flex w-full flex-col gap-3">
          <span className="text-muted-foreground px-1 text-xs font-semibold tracking-wider uppercase">
            {t`What you get`}
          </span>
          {/* Elongated full-width cards (one per deliverable: profile, position, teams), matching the
              "how it works" rows above — not a 2-col grid. */}
          <ul className="flex flex-col gap-3">
            {perks.map((perk, index) => (
              <Appear
                as="li"
                key={perk.title}
                // Continue the cascade from the three "how it works" cards.
                delay={(steps.length + index) * 140}
                className="bg-card flex items-center gap-4 rounded-xl border p-4"
              >
                <div className="bg-muted text-foreground/70 flex size-12 shrink-0 items-center justify-center rounded-xl">
                  <perk.icon className="size-6" strokeWidth={1.5} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="font-semibold">{perk.title}</span>
                  <span className="text-muted-foreground text-sm">{perk.desc}</span>
                </div>
              </Appear>
            ))}
          </ul>
        </div>

        <div className="flex w-full flex-col gap-2">
          <Button type="button" onClick={onPrimary} className="h-12 w-full text-base font-semibold">
            {anyStarted ? t`Continue where you left off` : t`Start the competencies test`}
            <ArrowRightIcon className="size-5" />
          </Button>
          <p className="text-muted-foreground text-center text-xs">{t`No pressure — your answers save automatically.`}</p>
        </div>
      </div>
    </section>
  )
}
